// native
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

const DEVICE_WAITING_TIMEOUT = 30 * 1000;

@Injectable({
  providedIn: 'root'
})
export class TechallDeviceSessionService {

  private localStream!: MediaStream;
  private peerConnection!: RTCPeerConnection;
  private socket!: WebSocket;
  private isListeningForDevice: boolean = false;
  private rtcConfig = {};

  deviceCallEnded$: Subject<void> = new Subject<void>();
  deviceCallStarted$: Subject<void> = new Subject<void>();
  deviceVideoBlocked$: Subject<boolean> = new BehaviorSubject<boolean>(false);
  deviceAudioBlocked$: Subject<boolean> = new BehaviorSubject<boolean>(false);
  deviceConnectionFailed$: Subject<boolean> = new BehaviorSubject<boolean>(false);

  isAudioBlockedByUser: boolean = false;
  isVideoBlockedByUser: boolean = false;

  constructor() {
    window.addEventListener("beforeunload", (event) => {
      this.endCall();
    });
  }

  async initializeDeviceCall(sessionId: number, callAgain: boolean = false) {
    await this.setupLocalStream();
    this.closeWebSocket();
    this.setupWebSocket(sessionId, callAgain);
    this.createDeviceReadyTimeout();
  }

  setupWebSocket(sessionId: number, callAgain: boolean) {
    const protocol = isDevMode() ? 'ws' : 'wss';
    this.socket = new WebSocket(`${protocol}://${window.location.host}/live/techall-session/${sessionId}`);

    this.socket.onmessage = async (message) => {
      const data = JSON.parse(message.data);
      if (data.type === 'deviceReady' && this.isListeningForDevice) {
        this.isListeningForDevice = false;
        this.deviceConnectionFailed$.next(false);
        this.deviceCallStarted$.next();
        await this.startCall();
      }
      else if (data.type === 'rtcConfig') {
        this.rtcConfig = data.config;
      }
      else if (data.type === 'answer') {
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
      }
      else if (data.type === 'candidate') {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
      else if (data.type === 'endCall') {
        this.deviceCallEnded$.next();
        this.clearExistingWebRTCConnection();
        this.stopLocalStream();
      }
      else if (data.type === 'restartCall') {
        await this.startCall();
      }
      else if (data.type === 'unityAllowVideoStream') {
        this.deviceVideoBlocked$.next(false);
        if (!this.isVideoBlockedByUser)
          this.localStream?.getVideoTracks().forEach(track => track.enabled = true);
      }
      else if (data.type === 'unityBlockVideoStream') {
        this.deviceVideoBlocked$.next(true);
        this.localStream?.getVideoTracks().forEach(track => track.enabled = false);
      }
      else if (data.type === 'unityAllowAudioStream') {
        this.deviceAudioBlocked$.next(false);
        if (!this.isAudioBlockedByUser)
          this.localStream?.getAudioTracks().forEach(track => track.enabled = true);
      }
      else if (data.type === 'unityBlockAudioStream') {
        this.localStream?.getAudioTracks().forEach(track => track.enabled = false);
        this.deviceAudioBlocked$.next(true);
      }
    };

    this.socket.addEventListener('open', (event) => {
      if (callAgain)
        this.socket.send(JSON.stringify({ type: 'startCall' }));
    });
  }

  async setupLocalStream() {
    if (!this.localStream)
      this.localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    this.renderStreamInDiv(document.getElementById('video_local'), this.localStream, true);
  }

  async createNewWebRTCConnection() {
    this.peerConnection = new RTCPeerConnection(this.rtcConfig);

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.send(JSON.stringify({ type: 'candidate', candidate: event.candidate }));
      }
    };

    this.peerConnection.ontrack = (event) => {
      const remoteContainer = document.getElementById('video_renderer');

      if (event.streams.length === 0) {
        if (!remoteContainer) return;
        let newStream = new MediaStream();
        newStream.addTrack(event.track);
        this.renderStreamInDiv(remoteContainer, newStream);
      } else {
        this.renderStreamInDiv(remoteContainer, event.streams[0]);
      }
    };

    this.peerConnection.onconnectionstatechange = () => {
      switch (this.peerConnection.connectionState) {
        case 'disconnected':
        case 'failed':
        case 'closed':
          this.isListeningForDevice = true;
          this.deviceConnectionFailed$.next(true);
          setTimeout(() => {
            if (this.isListeningForDevice) {
              this.deviceCallEnded$.next();
              this.clearExistingWebRTCConnection();
            }

          }, DEVICE_WAITING_TIMEOUT);
          break;
      }
    };

    this.localStream.getTracks().forEach(track => this.peerConnection.addTrack(track, this.localStream));
  }

  private async startCall() {
    // No stream means the call has ended and its tracks are stopped -- a late
    // restartCall must not resurrect the call with a track-less peer connection.
    // The next initializeDeviceCall() re-acquires media and starts fresh.
    if (!this.localStream)
      return;

    this.clearExistingWebRTCConnection();
    this.createNewWebRTCConnection();

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    this.socket.send(JSON.stringify({ type: 'offer', offer }));
    this.startVideo();
    this.unmute();
  }

  endCall() {
    this.clearExistingWebRTCConnection();
    this.stopLocalStream();
    this.socket?.send(JSON.stringify({ type: 'endCall' }));
  }

  clearExistingWebRTCConnection() {
    this.isListeningForDevice = false;
    this.deviceConnectionFailed$.next(false);
    if (this.peerConnection) {
      this.peerConnection.ontrack = null;
      this.peerConnection.onicecandidate = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
  }

  private stopLocalStream(): void {
    // Explicitly stop the camera/mic tracks so the device indicator turns off when
    // the call ends -- closing the RTCPeerConnection does not stop getUserMedia
    // tracks. localStream is nulled so setupLocalStream() re-acquires on the next call.
    this.localStream?.getTracks().forEach(track => track.stop());
    this.localStream = null;
  }

  closeWebSocket() {
    if (this.socket) {
      this.socket.onmessage = null;
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;

      this.socket.close();
      this.socket = null;
    }
  }

  mute() {
    this.localStream.getAudioTracks().forEach(track => track.enabled = false);
  }

  unmute() {
    this.localStream.getAudioTracks().forEach(track => track.enabled = true);
  }

  stopVideo() {
    this.localStream.getVideoTracks().forEach(track => track.enabled = false);
    this.socket.send(JSON.stringify({ type: 'hideCamera' }));
  }

  startVideo() {
    this.localStream.getVideoTracks().forEach(track => track.enabled = true);
    this.socket.send(JSON.stringify({ type: 'showCamera' }));
  }

  private renderStreamInDiv(container: HTMLElement | null, stream: MediaStream, mute: boolean = false) {
    if (!container) return;

    const existingVideo = container.querySelector('video');
    if (existingVideo)
      existingVideo.remove();

    const videoElement = document.createElement('video');
    videoElement.srcObject = stream;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    videoElement.style.objectFit = 'cover';

    if (mute)
      videoElement.volume = 0;

    container.appendChild(videoElement);
  }

  private createDeviceReadyTimeout() {
    this.isListeningForDevice = true;
    setTimeout(() => {
      if (this.isListeningForDevice) {
        this.isListeningForDevice = false;
        this.deviceCallEnded$.next();
        this.clearExistingWebRTCConnection();
      }
    }, DEVICE_WAITING_TIMEOUT);
  }
}
